import { useState, useEffect, useCallback } from 'react';
import { useBlocker, useSearchParams } from 'react-router-dom';
import { apiFetch } from '../../../../utils/api';
import { useRuleManager } from './hooks';
import RuleManagerSidebar from './RuleManagerSidebar';
import RuleEditor from './RuleEditor';
import RuleManagerModals from './RuleManagerModals';

/**
 * RuleManager - Main component for managing rule tables
 * 
 * This component manages:
 * - Family and ruleset selection
 * - Rule table editing (Excel-like grid)
 * - Import/export functionality
 * - Consistency checking
 * - Feature management
 */
export default function RuleManager({
    onDataChanged,
    featuresMetadata: featuresMetadataProp,
    currentContext: currentContextProp,
    setCurrentContext: setCurrentContextProp,
    isRightPaneOpen,
    setIsRightPaneOpen,
    isTableFocusMode = false,
    setIsTableFocusMode,
    rulesetSyncCount = 0,
    featuresRefreshCount = 0,
    onBindTreeSearch,
    onFeaturesLoaded
}) {
    // Use custom hook for state management
    const {
        // State
        activeProjectId,
        families,
        ruleSets,
        activeRuleSetId,
        rulesetSearchQuery,
        rulesetSearchMode,
        rulesetFeatureFilters,
        rulesetValueFilters,
        searchHighlight,
        rulesetSortBy,
        rulesetSortOrder,
        activeRuleSetData,
        isRuleSetLoading,
        ruleSetLoadFailed,
        featuresMetadata,
        originalRuleSetData,
        draftRuleSetData,
        ruleSetNotes,
        focusedCell,
        isEditing,
        editingCellValue,
        dropdownCoords,
        highlightedOptionIndex,
        modalType,
        modalTargetFamily,
        modalTargetRuleSet,
        modalTargetFolder,
        modalInputVal,
        deleteRuleSetsCheck,
        isSaving,
        consistencyResult,
        isCheckingConsistency,
        hasChanges,
        blocker,
        
        // Setters
        setRulesetSearchQuery,
        setRulesetSearchMode,
        setRulesetFeatureFilters,
        setRulesetValueFilters,
        setSearchHighlight,
        setRulesetSortBy,
        setRulesetSortOrder,
        setActiveRuleSetId,
        setActiveRuleSetData,
        setDraftRuleSetData,
        setFocusedCell,
        setIsEditing,
        setEditingCellValue,
        setDropdownCoords,
        setHighlightedOptionIndex,
        setModalType,
        setModalTargetFamily,
        setModalInputVal,
        setDeleteRuleSetsCheck,
        
        // Handlers
        loadMenu,
        handleOpenModal,
        handleOpenCreateFolder,
        handleOpenRenameFolder,
        handleOpenCreateRulesetInFolder,
        handleCreateFolderSubmit,
        handleRenameFolderSubmit,
        handleDeleteFolder,
        handleMoveRuleSetToFolder,
        handleCreateRuleSetSubmit,
        handleRenameFamilySubmit,
        handleDeleteFamilySubmit,
        handleCheckConsistency,
        handleSaveChanges,
        handleCellKeyDown,
        handleCommitCell,
        handleEditorKeyDown,
        handleDeleteRowLocally,
        handleAddFeatureLocally,
        handleDeleteFeatureLocally,
        handleDeleteRuleSet,
        handleCopyRuleSetToClipboard,
        handleOpenAddComment,
        handleOpenRenameRuleSet,
        handleAddCommentSubmit,
        handleRenameRuleSetSubmit,
        reloadActiveRuleSet,
        applyTreeSearch,
        
        // Current context management
        currentContext,
        setCurrentContext
    } = useRuleManager({
        onDataChanged,
        onFeaturesLoaded,
        currentContext: currentContextProp,
        setCurrentContext: setCurrentContextProp,
        featuresMetadata: featuresMetadataProp,
        rulesetSyncCount,
        featuresRefreshCount
    });

    // Sync with prop changes
    useEffect(() => {
        if (currentContextProp && currentContextProp.id !== currentContext?.id) {
            setCurrentContext(currentContextProp);
        }
    }, [currentContextProp, setCurrentContext]);

    useEffect(() => {
        if (!onBindTreeSearch) return undefined;
        onBindTreeSearch(applyTreeSearch);
        return () => onBindTreeSearch(null);
    }, [onBindTreeSearch, applyTreeSearch]);

    useEffect(() => {
        if (!draftRuleSetData && isTableFocusMode) {
            setIsTableFocusMode?.(false);
        }
    }, [draftRuleSetData, isTableFocusMode, setIsTableFocusMode]);

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <div style={{ display: 'flex', height: '100%', width: '100%', overflow: 'hidden', flex: 1 }}>
            {/* Column 1: Rules Selector (Sidebar) */}
            <div
                className={`rules-focus-clip${isTableFocusMode ? ' is-collapsed' : ''}`}
                aria-hidden={isTableFocusMode}
                inert={isTableFocusMode || undefined}
            >
                <div className="rules-focus-clip-inner">
                    <RuleManagerSidebar
                        families={families}
                        ruleSets={ruleSets}
                        activeRuleSetId={activeRuleSetId}
                        currentContext={currentContext}
                        rulesetSearchQuery={rulesetSearchQuery}
                        rulesetSearchMode={rulesetSearchMode}
                        rulesetFeatureFilters={rulesetFeatureFilters}
                        rulesetValueFilters={rulesetValueFilters}
                        rulesetSortBy={rulesetSortBy}
                        rulesetSortOrder={rulesetSortOrder}
                        activeFamily={families.find(f => f.id.toString() === currentContext?.id?.toString())}
                        handleOpenModal={handleOpenModal}
                        handleOpenCreateFolder={handleOpenCreateFolder}
                        handleOpenRenameFolder={handleOpenRenameFolder}
                        handleOpenCreateRulesetInFolder={handleOpenCreateRulesetInFolder}
                        handleDeleteFolder={handleDeleteFolder}
                        handleMoveRuleSetToFolder={handleMoveRuleSetToFolder}
                        setActiveRuleSetId={setActiveRuleSetId}
                        setRulesetSearchQuery={setRulesetSearchQuery}
                        setRulesetSearchMode={setRulesetSearchMode}
                        setRulesetFeatureFilters={setRulesetFeatureFilters}
                        setRulesetValueFilters={setRulesetValueFilters}
                        setSearchHighlight={setSearchHighlight}
                        setRulesetSortBy={setRulesetSortBy}
                        setRulesetSortOrder={setRulesetSortOrder}
                        setCurrentContext={setCurrentContext}
                        loadMenu={loadMenu}
                        featuresMetadata={featuresMetadata}
                        activeRuleSetData={activeRuleSetData}
                        onDataChanged={onDataChanged}
                        handleCheckConsistency={handleCheckConsistency}
                        draftRuleSetData={draftRuleSetData}
                        handleCopyRuleSetToClipboard={handleCopyRuleSetToClipboard}
                        handleOpenAddComment={handleOpenAddComment}
                        handleOpenRenameRuleSet={handleOpenRenameRuleSet}
                        handleDeleteRuleSet={handleDeleteRuleSet}
                        reloadActiveRuleSet={reloadActiveRuleSet}
                        ruleSetNotes={ruleSetNotes}
                    />
                </div>
            </div>

            {/* Column 2: Main Rule Editor Pane */}
            <RuleEditor
                draftRuleSetData={draftRuleSetData}
                activeRuleSetData={activeRuleSetData}
                activeRuleSetId={activeRuleSetId}
                isRuleSetLoading={isRuleSetLoading}
                ruleSetLoadFailed={ruleSetLoadFailed}
                featuresMetadata={featuresMetadata}
                focusedCell={focusedCell}
                isEditing={isEditing}
                editingCellValue={editingCellValue}
                dropdownCoords={dropdownCoords}
                highlightedOptionIndex={highlightedOptionIndex}
                families={families}
                currentContext={currentContext}
                isRightPaneOpen={isRightPaneOpen}
                setIsRightPaneOpen={setIsRightPaneOpen}
                isTableFocusMode={isTableFocusMode}
                setIsTableFocusMode={setIsTableFocusMode}
                handleCellKeyDown={handleCellKeyDown}
                handleCommitCell={handleCommitCell}
                handleEditorKeyDown={handleEditorKeyDown}
                handleDeleteRowLocally={handleDeleteRowLocally}
                handleAddFeatureLocally={handleAddFeatureLocally}
                handleDeleteFeatureLocally={handleDeleteFeatureLocally}
                handleSaveChanges={handleSaveChanges}
                setFocusedCell={setFocusedCell}
                setIsEditing={setIsEditing}
                setEditingCellValue={setEditingCellValue}
                setHighlightedOptionIndex={setHighlightedOptionIndex}
                setDraftRuleSetData={setDraftRuleSetData}
                hasChanges={hasChanges}
                originalRuleSetData={originalRuleSetData}
                blocker={blocker}
                ruleSetNotes={ruleSetNotes}
                searchHighlight={searchHighlight}
            />

            {/* Modals */}
            <RuleManagerModals
                modalType={modalType}
                modalTargetFamily={modalTargetFamily}
                modalTargetRuleSet={modalTargetRuleSet}
                modalTargetFolder={modalTargetFolder}
                modalInputVal={modalInputVal}
                deleteRuleSetsCheck={deleteRuleSetsCheck}
                isSaving={isSaving}
                isCheckingConsistency={isCheckingConsistency}
                consistencyResult={consistencyResult}
                featuresMetadata={featuresMetadata}
                families={families}
                activeRuleSetData={activeRuleSetData}
                activeRuleSetId={activeRuleSetId}
                currentContext={currentContext}
                draftRuleSetData={draftRuleSetData}
                originalRuleSetData={originalRuleSetData}
                blocker={blocker}
                handleCreateRuleSetSubmit={handleCreateRuleSetSubmit}
                handleCreateFolderSubmit={handleCreateFolderSubmit}
                handleRenameFolderSubmit={handleRenameFolderSubmit}
                handleRenameRuleSetSubmit={handleRenameRuleSetSubmit}
                handleRenameFamilySubmit={handleRenameFamilySubmit}
                handleDeleteFamilySubmit={handleDeleteFamilySubmit}
                handleAddCommentSubmit={handleAddCommentSubmit}
                handleSaveChanges={handleSaveChanges}
                setModalType={setModalType}
                setModalInputVal={setModalInputVal}
                setDeleteRuleSetsCheck={setDeleteRuleSetsCheck}
                setDraftRuleSetData={setDraftRuleSetData}
                loadMenu={loadMenu}
                onDataChanged={onDataChanged}
                setActiveRuleSetId={setActiveRuleSetId}
                setActiveRuleSetData={setActiveRuleSetData}
            />
        </div>
    );
}
